/** 一覧シートに並べるルートプラン 1 件のカード */

import { format } from 'date-fns'
import {
  Clock,
  Copy,
  Eye,
  EyeOff,
  Loader2,
  Luggage,
  MapPin,
  MoreHorizontal,
  Pencil,
  Route,
  Share2,
  Trash2,
  Users,
} from 'lucide-react'

import type { RoutePlanSummary } from '@/api/types/route-plan'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { CustomDropdown } from '@/components/ui/custom-dropdown'
import { parseUtcDate } from '@/lib/date-utils'
import { cn } from '@/lib/utils'

import { formatDistance, formatDuration, planColor, planDateKey, toJstDateKey } from '../lib'
import { SelectBox } from './select-box'

const STATUS_LABEL: Record<RoutePlanSummary['status'], string> = {
  draft: '下書き',
  optimized: '最適化済み',
  done: '完了',
}

/** plan_date は日付だけの文字列なので Date を通さずそのまま整形する */
const formatPlanDate = (value: string | null): string | null => {
  if (!value) return null
  const [, month, day] = value.split('-')
  return month && day ? `${Number(month)}/${Number(day)}` : value
}

const formatDepartTime = (value: string | null): string | null => {
  if (!value) return null
  return parseUtcDate(value).toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' })
}

/** 複製したプラン同士を見分けられるよう秒まで出す */
const formatUpdatedAt = (value: string): string => {
  const date = parseUtcDate(value)
  return format(date, date.getFullYear() === new Date().getFullYear() ? 'M/d HH:mm:ss' : 'yyyy/M/d HH:mm:ss')
}

/** 行ったあとに記録するものなので、まだ来ていない予定には出さない */
const canRecordTrip = (plan: RoutePlanSummary): boolean => {
  const key = planDateKey(plan)
  return key !== null && key <= toJstDateKey(new Date())
}

type Props = {
  plan: RoutePlanSummary
  onClick: () => void
  onTrip: () => void
  isCreatingTrip: boolean
  onDuplicate: () => void
  isDuplicating: boolean
  onShare: () => void
  onDelete: () => void
  isDeleting: boolean
  /** 地図に線を出しているか */
  isShownOnMap: boolean
  onToggleMap: () => void
  isSelected: boolean
  onToggleSelect: () => void
}

export const PlanCard = ({
  plan,
  onClick,
  onTrip,
  isCreatingTrip,
  onDuplicate,
  isDuplicating,
  onShare,
  onDelete,
  isDeleting,
  isShownOnMap,
  onToggleMap,
  isSelected,
  onToggleSelect,
}: Props) => {
  const duration = formatDuration(plan.total_duration_sec)
  const distance = formatDistance(plan.total_distance_m)
  const date = formatPlanDate(plan.plan_date)
  const departAt = formatDepartTime(plan.depart_at)
  const updatedAt = formatUpdatedAt(plan.updated_at)
  const showTrip = plan.trip_id !== null || canRecordTrip(plan)
  const color = planColor(plan.id)

  return (
    <div
      className={cn(
        'relative border-b transition-colors hover:bg-muted/30',
        isSelected && 'bg-primary/5 hover:bg-primary/10',
      )}
    >
      <div className="flex">
        <SelectBox
          checked={isSelected}
          onToggle={onToggleSelect}
          label={`「${plan.title}」を選択`}
          className="mt-4 ml-2"
        />
        <button
          onClick={onClick}
          className={cn('flex min-w-0 flex-1 flex-col gap-2 py-3.5 pr-3 pl-1 text-left', !isShownOnMap && 'opacity-60')}
        >
          <div className="flex items-center gap-2 pr-18">
            <div
              className="flex size-9 shrink-0 items-center justify-center rounded-lg"
              style={{ backgroundColor: `${color}26` }}
            >
              <Route size={18} style={{ color }} />
            </div>
            <span className="min-w-0 flex-1 truncate font-medium">{plan.title}</span>
            <Badge variant={plan.status === 'draft' ? 'outline' : 'secondary'} className="shrink-0">
              {STATUS_LABEL[plan.status]}
            </Badge>
          </div>

          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
            {plan.owner_name && (
              <span className="flex items-center gap-1">
                <Users size={12} />
                {plan.owner_name}
              </span>
            )}
            {date && <span className="tabular-nums">{date}</span>}
            {departAt && (
              <span className="flex items-center gap-1 tabular-nums">
                <Clock size={12} />
                {departAt} 出発
              </span>
            )}
            <span className="flex items-center gap-1 tabular-nums">
              <MapPin size={12} />
              {plan.item_count ?? 0} スポット
            </span>
            {duration && <span className="tabular-nums">{duration}</span>}
            {distance && <span className="tabular-nums">{distance}</span>}
            <span className="flex items-center gap-1 tabular-nums">
              <Pencil size={12} />
              {updatedAt}
            </span>
          </div>
        </button>
      </div>

      <div className="absolute top-4 right-2 flex items-center">
        <button
          type="button"
          onClick={onToggleMap}
          aria-pressed={isShownOnMap}
          aria-label={isShownOnMap ? '地図から隠す' : '地図に表示する'}
          className="flex size-8 items-center justify-center rounded-md text-muted-foreground"
        >
          {isShownOnMap ? <Eye size={18} /> : <EyeOff size={18} />}
        </button>

        {plan.is_owner && (
          <CustomDropdown
            sections={[
              {
                type: 'action',
                label: 'プラン',
                items: [
                  {
                    key: 'duplicate',
                    label: isDuplicating ? '複製中…' : '複製',
                    icon: <Copy size={16} />,
                    onClick: onDuplicate,
                  },
                  {
                    key: 'share',
                    label: '共有',
                    icon: <Share2 size={16} />,
                    onClick: onShare,
                  },
                  {
                    key: 'delete',
                    label: isDeleting ? '削除中…' : '削除',
                    icon: <Trash2 size={16} className="text-destructive" />,
                    onClick: onDelete,
                  },
                ],
              },
            ]}
            triggerIcon={
              <span className="flex size-8 items-center justify-center rounded-md text-muted-foreground">
                {isDuplicating || isDeleting ? (
                  <Loader2 size={18} className="animate-spin" />
                ) : (
                  <MoreHorizontal size={18} />
                )}
              </span>
            }
            contentClassName="z-[10000000]"
          />
        )}
      </div>

      {showTrip && plan.is_owner && (
        <div className="-mt-1.5 pb-2 pl-9">
          <Button variant="ghost" size="sm" className="gap-1.5" disabled={isCreatingTrip} onClick={onTrip}>
            {isCreatingTrip ? <Loader2 size={14} className="animate-spin" /> : <Luggage size={14} />}
            {plan.trip_id !== null ? '旅の記録を開く' : '旅の記録を作る'}
          </Button>
        </div>
      )}
    </div>
  )
}
